import React from 'react';
import PropTypes from 'prop-types';
import { Alert, Box, Chip, Stack, Typography } from '@mui/material';
import { CONFIDENCE_THRESHOLD } from '../config/confidenceThreshold';

const CLINICAL_DISCLAIMER =
  'For reference by qualified clinicians only. Not a substitute for clinical judgment. Verify all information against primary sources and local protocol before acting.';

export function SourceChip({ source, retrievedAt }) {
  const dateLabel = retrievedAt
    ? new Date(retrievedAt).toLocaleDateString()
    : 'unknown date';
  return (
    <Chip
      size="small"
      variant="outlined"
      label={`${source || 'unknown'} · retrieved ${dateLabel}`}
      sx={{ mb: 1 }}
    />
  );
}

SourceChip.propTypes = {
  source: PropTypes.string,
  retrievedAt: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

export function RequiresReviewBanner({ confidenceScore, requiresReview, reason }) {
  if (!requiresReview && (confidenceScore == null || confidenceScore >= CONFIDENCE_THRESHOLD)) {
    return null;
  }
  return (
    <Alert severity="warning" sx={{ mt: 1 }}>
      Requires extra confirmation
      {confidenceScore != null ? ` (confidence ${confidenceScore.toFixed(2)})` : ''}
      {reason ? ` — ${reason}` : ''}
    </Alert>
  );
}

RequiresReviewBanner.propTypes = {
  confidenceScore: PropTypes.number,
  requiresReview: PropTypes.bool,
  reason: PropTypes.string,
};

export default function DrugClinicalDataShell({ children, countryCode, confidenceScore, requiresReview }) {
  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        {CLINICAL_DISCLAIMER}
      </Alert>
      {countryCode && (
        <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <Typography variant="body2" color="text.secondary">
            Context:
          </Typography>
          <Chip size="small" label={`Emergency Medicine · ${countryCode}`} />
          {confidenceScore != null && (
            <Chip
              size="small"
              color={confidenceScore >= CONFIDENCE_THRESHOLD ? 'success' : 'warning'}
              label={`Confidence ${confidenceScore.toFixed(2)}`}
            />
          )}
        </Stack>
      )}
      <RequiresReviewBanner
        confidenceScore={confidenceScore}
        requiresReview={requiresReview}
      />
      {children}
    </Box>
  );
}

DrugClinicalDataShell.propTypes = {
  children: PropTypes.node,
  countryCode: PropTypes.string,
  confidenceScore: PropTypes.number,
  requiresReview: PropTypes.bool,
};

export { CLINICAL_DISCLAIMER };
